import { supabase } from '@/integrations/supabase/client';

/**
 * Attempt to send the founding member congratulations email.
 * Idempotent — the Edge Function checks email_logs before sending.
 * Fire-and-forget — never throws.
 */
async function trySendFoundingMemberEmail(userId: string): Promise<void> {
  try {
    await supabase.functions.invoke('send-founding-member-email', {
      body: { userId },
    });
  } catch {
    // Fire-and-forget — email failure shouldn't block the UI
  }
}

/**
 * Sync the user's current habit streak to founding_member_progress.
 * Fire-and-forget — never throws.
 */
export async function syncFoundingMemberStreak(userId: string, currentStreak: number): Promise<void> {
  try {
    await supabase.rpc('sync_founding_member_streak', {
      p_user_id: userId,
      p_current_streak: currentStreak,
    });
  } catch (error) {
    console.error('Failed to sync founding member streak:', error);
  }
  await trySendFoundingMemberEmail(userId);
}

/**
 * Sync the user's completed (archived) goal count to founding_member_progress.
 * Fire-and-forget — never throws.
 */
export async function syncFoundingMemberGoals(userId: string): Promise<void> {
  try {
    await supabase.rpc('sync_founding_member_goals', {
      p_user_id: userId,
    });
  } catch (error) {
    console.error('Failed to sync founding member goals:', error);
  }
  await trySendFoundingMemberEmail(userId);
}

/**
 * Sync the user's referral count to founding_member_progress.
 * Fire-and-forget — never throws.
 */
export async function syncFoundingMemberReferrals(userId: string): Promise<void> {
  try {
    await supabase.rpc('sync_founding_member_referrals', {
      p_user_id: userId,
    });
  } catch (error) {
    console.error('Failed to sync founding member referrals:', error);
  }
  await trySendFoundingMemberEmail(userId);
}

/**
 * Sync the user's feedback count to founding_member_progress.
 * Counts feedback from: feedback table, NPS responses with qualitative feedback, testimonials.
 * Fire-and-forget — never throws.
 */
export async function syncFoundingMemberFeedback(userId: string): Promise<void> {
  try {
    await supabase.rpc('sync_founding_member_feedback', {
      p_user_id: userId,
    });
  } catch (error) {
    console.error('Failed to sync founding member feedback:', error);
  }
  await trySendFoundingMemberEmail(userId);
}
